"use client";

import type { ReactNode } from "react";

/** One chip in the meta row: icon + short value. */
export interface EventMetaItem {
  key: "date" | "time" | "productora" | "city" | "age";
  value: string;
  /** Screen-reader label (e.g. "Fecha"). The visible chip shows only the value. */
  label: string;
}

const STROKE = { stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const ICONS: Record<EventMetaItem["key"], ReactNode> = {
  date: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="3" {...STROKE} />
      <path d="M3 10h18M8 3v4M16 3v4" {...STROKE} />
    </svg>
  ),
  time: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" {...STROKE} />
      <path d="M12 7v5l3 2" {...STROKE} />
    </svg>
  ),
  productora: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 18V6l10-2v12" {...STROKE} />
      <circle cx="6.5" cy="18" r="2.5" {...STROKE} />
      <circle cx="16.5" cy="16" r="2.5" {...STROKE} />
    </svg>
  ),
  city: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" {...STROKE} />
      <circle cx="12" cy="10" r="2.5" {...STROKE} />
    </svg>
  ),
  age: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" {...STROKE} />
      <path d="M12 8v5M12 16h.01" {...STROKE} />
    </svg>
  ),
};

/**
 * Compact icon + value chips under the event title (date · time · productora ·
 * city · age), mirroring the mobile event detail meta row. Chips enter with a
 * small stagger; the age chip is only rendered when the event has a minimum age.
 */
export function EventMetaRow({ items, ariaLabel }: { items: EventMetaItem[]; ariaLabel: string }) {
  if (items.length === 0) return null;
  return (
    <ul className="gruv-event__meta gruv-stagger" data-testid="event-meta" aria-label={ariaLabel}>
      {items.map((item) => (
        <li key={item.key} className={`gruv-event__meta-chip gruv-event__meta-chip--${item.key}`}>
          <span className="gruv-event__meta-icon">{ICONS[item.key]}</span>
          <span className="sr-only">{item.label}: </span>
          <span className="gruv-event__meta-value">{item.value}</span>
        </li>
      ))}
    </ul>
  );
}
